import React from "react";
import { Pencil, Trash2, Plus, Star, MapPinCheck, MapPinOff } from "lucide-react";
import { addressIdEquals, getAddressId, isAddressDefault } from "../../utils/address";
import { ListSkeleton } from "../skeletons/ShopperSkeletons";

/**
 * Formats a DB address row into a single-line string.
 */
function formatAddress(addr) {
  const readPart = (...values) => {
    const value = values.find((entry) => typeof entry === "string" && entry.trim());
    return value ? value.trim() : "";
  };

  return [
    readPart(addr.address_line1, addr.addressLine1, addr.street_address, addr.streetAddress),
    readPart(addr.address_line2, addr.addressLine2),
    readPart(addr.landmark),
    readPart(addr.barangay),
    readPart(addr.city, addr.city_municipality, addr.cityMunicipality),
    readPart(addr.province),
    readPart(addr.region),
    readPart(addr.postal_code, addr.postalCode),
  ]
    .filter(Boolean)
    .join(", ");
}

/**
 * AddressList – renders all saved addresses as selectable cards.
 *
 * Props:
 *  addresses    : address[] from API (snake_case fields)
 *  selectedId   : number | null
 *  onSelect(id) : callback
 *  onEdit(addr) : callback
 *  onDelete(id) : callback (triggers confirmation inline)
 *  onSetDefault(id) : callback to set as default address
 *  onAddNew()   : callback
 *  isDark       : boolean
 *  loading      : boolean
 */
export default function AddressList({
  addresses,
  selectedId,
  onSelect,
  onEdit,
  onDelete,
  onSetDefault,
  onAddNew,
  loading,
}) {
  if (loading) {
    return <ListSkeleton rows={2} label="Loading saved addresses" className="space-y-3 py-2" />;
  }

  return (
    <div className="space-y-3">
      {addresses.map((addr, index) => {
        const addressId = getAddressId(addr);
        const active = addressIdEquals(selectedId, addressId);
        const isDefault = isAddressDefault(addr);
        const pinConfirmed = Boolean(addr.pin_confirmed_at) && addr.location_quality === "CONFIRMED";

        const handleSelect = () => {
          if (addressId != null) {
            onSelect(addressId);
          }
        };
        
        return (
          <div
            key={addressId ?? `${addr.full_name || "address"}-${addr.phone || "no-phone"}-${index}`}
            role="button"
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={(event) => {
              if (addressId == null) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleSelect();
              }
            }}
            className={[
              "w-full text-left rounded-xl border p-4 transition relative",
              active
                ? "border-neutral-950 bg-neutral-100 dark:border-white dark:bg-neutral-800"
                : "border-neutral-200 bg-white hover:border-neutral-300 dark:border-neutral-700 dark:bg-transparent dark:hover:border-neutral-500",
            ].join(" ")}
          >
            <div className="flex gap-3 items-start">
              {/* Radio dot */}
              <span
                className={[
                  "mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition",
                  active
                    ? "border-neutral-950 bg-neutral-950 dark:border-white dark:bg-white"
                    : "border-neutral-300 dark:border-neutral-600",
                ].join(" ")}
              >
                {active && (
                  <span className="h-1.5 w-1.5 rounded-full bg-white dark:bg-neutral-950" />
                )}
              </span>

              {/* Content */}
              <div className="flex-1 min-w-0 pr-12">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                    {addr.full_name}
                  </span>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {addr.phone}
                  </span>
                  {isDefault ? (
                  <span className="rounded-full border border-neutral-300 bg-neutral-50 px-2 py-0.5 text-[10px] font-bold text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                      DEFAULT
                    </span>
                  ) : null}
                  {addr.label ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-neutral-200 text-neutral-500 uppercase dark:border-neutral-700 dark:text-neutral-400">
                      {addr.label}
                    </span>
                  ) : null}
                  <span className={[
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                    pinConfirmed
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                      : "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
                  ].join(" ")}>
                    {pinConfirmed ? <MapPinCheck size={11} /> : <MapPinOff size={11} />}
                    {pinConfirmed ? "PIN CONFIRMED" : "PIN REQUIRED"}
                  </span>
                </div>
                <p className="text-xs mt-1 line-clamp-2 leading-relaxed text-neutral-500 dark:text-neutral-400">
                  {formatAddress(addr)}
                </p>
                
                {/* Set as Default button (only for non-default addresses) */}
                {!isDefault && onSetDefault && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (addressId != null) {
                        onSetDefault(addressId);
                      }
                    }}
                    className={`mt-2 flex items-center gap-1 text-[11px] font-medium transition ${
                      "text-neutral-600 hover:text-neutral-950 dark:text-neutral-400 dark:hover:text-white"
                    }`}
                  >
                    <Star size={11} />
                    Set as Default
                  </button>
                )}
              </div>
            </div>

            {/* Edit / Delete actions (absolute top-right) */}
            <div className="absolute top-3 right-3 flex gap-1.5">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onEdit(addr); }}
                className="p-1.5 rounded-lg transition text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-500 dark:hover:text-neutral-200 dark:hover:bg-neutral-700"
                aria-label="Edit address"
              >
                <Pencil size={13} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (addressId != null) {
                    onDelete(addressId);
                  }
                }}
                className="p-1.5 rounded-lg transition text-neutral-400 hover:text-rose-500 hover:bg-rose-50 dark:text-neutral-500 dark:hover:text-rose-400 dark:hover:bg-rose-900/20"
                aria-label="Delete address"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        );
      })}

      {/* Add new */}
      <button
        type="button"
        onClick={onAddNew}
        className={[
          "w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-dashed text-sm font-medium transition",
          "border-neutral-300 text-neutral-500 hover:bg-neutral-50 hover:border-neutral-400 dark:border-neutral-600 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:border-neutral-500",
        ].join(" ")}
      >
        <Plus size={16} />
        Add New Address
      </button>
    </div>
  );
}

export { formatAddress };
